import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import tw from 'twin.macro';
import { useMutation, useQuery } from '@apollo/client';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowDown, faArrowUp, faFire } from '@fortawesome/free-solid-svg-icons';
import {
  CREATE_HOT_LOCATION,
  DELETE_HOT_LOCATION,
  GET_HOT_LOCATIONS,
  REORDER_HOT_LOCATIONS,
  UPDATE_HOT_LOCATION,
} from '../../services/hotLocationService/queries';
import { AddressInput, LocationData } from '../../components/addressInput';
import { Button } from '../../components/button';
import { Modal } from '../../components/modal';
import {
  Badge,
  Box,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  CardRow,
  EmptyState,
  Field,
  FormGrid,
  Input,
  LoadingBlock,
  PageContainer,
  PageHeader,
} from '../../components/ui';

const RowMain = styled.div`
  ${tw`flex items-center gap-4 min-w-0 flex-1`}
`;

const Position = styled.div`
  ${tw`flex items-center justify-center w-9 h-9 rounded-lg text-sm font-semibold shrink-0`}
  background-color: var(--color-bg-secondary);
  color: var(--color-text-primary);
  font-family: var(--font-display);
`;

const RowTitle = styled.div`
  ${tw`text-sm font-semibold`}
  color: var(--color-text-primary);
`;

const RowMeta = styled.div`
  ${tw`text-xs mt-0.5`}
  color: var(--color-text-secondary);
`;

const RowActions = styled.div`
  ${tw`flex items-center gap-1 shrink-0`}
`;

const IconButton = styled.button`
  ${tw`inline-flex items-center justify-center w-8 h-8 rounded-md border bg-transparent cursor-pointer`}
  border-color: var(--color-border);
  color: var(--color-text-secondary);
  transition:
    background-color var(--transition-normal),
    color var(--transition-normal);
  &:hover:not(:disabled) {
    background-color: var(--color-bg-secondary);
    color: var(--color-text-primary);
  }
  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const ModalTitle = styled.h3`
  ${tw`text-lg font-semibold mb-4`}
  color: var(--color-text-primary);
  font-family: var(--font-display);
`;

const ModalActions = styled.div`
  ${tw`flex flex-col-reverse sm:flex-row sm:justify-end gap-2 mt-6`}
`;

type HotLocationRow = {
  id: string;
  city: string;
  state: string;
  latitude: number;
  longitude: number;
  index: number;
  amount: number;
};

type FormState = {
  id?: string;
  city: string;
  state: string;
  latitude: number;
  longitude: number;
  index: number;
  amount: number;
  addressSearch: string;
};

const emptyForm = (): FormState => ({
  city: '',
  state: '',
  latitude: 0,
  longitude: 0,
  index: 1,
  amount: 12,
  addressSearch: '',
});

export default function AdminHotLocationsPage() {
  const { data, loading, refetch } = useQuery(GET_HOT_LOCATIONS, {
    fetchPolicy: 'network-only',
  });
  const [createHotLocation, { loading: creating }] = useMutation(CREATE_HOT_LOCATION);
  const [updateHotLocation, { loading: updating }] = useMutation(UPDATE_HOT_LOCATION);
  const [deleteHotLocation] = useMutation(DELETE_HOT_LOCATION);
  const [reorderHotLocations, { loading: reordering }] = useMutation(REORDER_HOT_LOCATIONS);
  const [rows, setRows] = useState<HotLocationRow[]>([]);
  const [orderDirty, setOrderDirty] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState<FormState>(emptyForm());

  const serverRows = useMemo(
    () => (data?.hotLocations ?? []) as HotLocationRow[],
    [data?.hotLocations],
  );

  React.useEffect(() => {
    if (!orderDirty) {
      setRows(serverRows);
    }
  }, [serverRows, orderDirty]);

  const usedIndexes = new Set(serverRows.map(row => row.index));
  const nextAvailableIndex = [1, 2, 3, 4, 5].find(index => !usedIndexes.has(index)) ?? 1;

  const openCreate = () => {
    setForm({ ...emptyForm(), index: nextAvailableIndex });
    setModalOpen(true);
  };

  const openEdit = (row: HotLocationRow) => {
    setForm({
      id: row.id,
      city: row.city,
      state: row.state,
      latitude: row.latitude,
      longitude: row.longitude,
      index: row.index,
      amount: row.amount,
      addressSearch: `${row.city}, ${row.state}`,
    });
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setForm(emptyForm());
  };

  const handlePlaceSelect = (location: LocationData) => {
    if (!location.latitude || !location.longitude) {
      toast.error('Could not resolve coordinates for that place');
      return;
    }
    setForm(current => ({
      ...current,
      addressSearch: location.formattedAddress || location.address,
      latitude: location.latitude,
      longitude: location.longitude,
      city: location.city || current.city,
      state: location.state || current.state,
    }));
  };

  const saveForm = async () => {
    if (!form.city.trim() || !form.state.trim() || !form.latitude || !form.longitude) {
      toast.error('Select a place so city, state, and coordinates are filled');
      return;
    }

    try {
      if (form.id) {
        await updateHotLocation({
          variables: {
            input: {
              id: form.id,
              city: form.city.trim(),
              state: form.state.trim(),
              latitude: form.latitude,
              longitude: form.longitude,
              index: form.index,
              amount: form.amount,
            },
          },
        });
        toast.success('Hot location updated');
      } else {
        await createHotLocation({
          variables: {
            input: {
              city: form.city.trim(),
              state: form.state.trim(),
              latitude: form.latitude,
              longitude: form.longitude,
              index: form.index,
              amount: form.amount,
            },
          },
        });
        toast.success('Hot location created');
      }
      closeModal();
      setOrderDirty(false);
      await refetch();
    } catch (error: any) {
      toast.error(error?.message || 'Failed to save hot location');
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Delete this hot location?')) {
      return;
    }
    try {
      await deleteHotLocation({ variables: { id } });
      toast.success('Hot location deleted');
      setOrderDirty(false);
      await refetch();
    } catch (error: any) {
      toast.error(error?.message || 'Failed to delete hot location');
    }
  };

  const moveRow = (fromIndex: number, toIndex: number) => {
    if (toIndex < 0 || toIndex >= rows.length) {
      return;
    }
    const next = Array.from(rows);
    const [moved] = next.splice(fromIndex, 1);
    next.splice(toIndex, 0, moved);
    setRows(next);
    setOrderDirty(true);
  };

  const saveOrder = async () => {
    try {
      await reorderHotLocations({
        variables: {
          input: {
            orderedIds: rows.map(row => row.id),
          },
        },
      });
      toast.success('Order saved');
      setOrderDirty(false);
      await refetch();
    } catch (error: any) {
      toast.error(error?.message || 'Failed to save order');
    }
  };

  return (
    <PageContainer width="lg" canvas>
      <PageHeader
        eyebrow="Admin"
        title="Hot locations"
        description="The monthly-rental city sections on the guest home page. Up to five cities; each one controls how many monthly cars appear for that city."
        actions={
          <Button
            text="Add location"
            type="button"
            onClick={openCreate}
            isDisabled={rows.length >= 5}
          />
        }
      />

      <Card>
        <CardHeader
          title="Configured markets"
          description="Use the arrows to reorder the sections, then save the order."
          actions={
            orderDirty ? (
              <Badge tone="warning" dot>
                Unsaved order
              </Badge>
            ) : undefined
          }
        />
        {loading ? (
          <LoadingBlock label="Loading hot locations" />
        ) : rows.length === 0 ? (
          <EmptyState
            icon={faFire}
            title="No hot locations yet"
            description="Add a city to show a monthly-rental section for it on the home page."
            action={<Button text="Add location" type="button" size="sm" onClick={openCreate} />}
          />
        ) : (
          rows.map((row, index) => (
            <CardRow key={row.id}>
              <RowMain>
                <Position>{row.index}</Position>
                <Box $minW0>
                  <RowTitle>
                    {row.city}, {row.state}
                  </RowTitle>
                  <RowMeta>
                    Up to {row.amount} cars · {row.latitude.toFixed(4)}, {row.longitude.toFixed(4)}
                  </RowMeta>
                </Box>
              </RowMain>
              <RowActions>
                <IconButton
                  type="button"
                  aria-label="Move up"
                  onClick={() => moveRow(index, index - 1)}
                  disabled={index === 0}
                >
                  <FontAwesomeIcon icon={faArrowUp} size="sm" />
                </IconButton>
                <IconButton
                  type="button"
                  aria-label="Move down"
                  onClick={() => moveRow(index, index + 1)}
                  disabled={index === rows.length - 1}
                >
                  <FontAwesomeIcon icon={faArrowDown} size="sm" />
                </IconButton>
                <Button
                  text="Edit"
                  theme="secondary"
                  size="sm"
                  type="button"
                  onClick={() => openEdit(row)}
                />
                <Button
                  text="Delete"
                  theme="text"
                  size="sm"
                  type="button"
                  onClick={() => handleDelete(row.id)}
                />
              </RowActions>
            </CardRow>
          ))
        )}
        {rows.length > 0 && (
          <CardFooter>
            <Button
              text="Save order"
              theme={orderDirty ? 'filled' : 'secondary'}
              size="sm"
              type="button"
              onClick={saveOrder}
              isDisabled={!orderDirty}
              isLoading={reordering}
            />
          </CardFooter>
        )}
      </Card>

      <Modal isOpen={modalOpen} onClose={closeModal} modalWidth="640px" title="Hot location">
        <ModalTitle>{form.id ? 'Edit hot location' : 'Add hot location'}</ModalTitle>

        <Box $mb={4}>
          <Field label="Search place" hint="Pick a place to fill city, state and coordinates.">
            <AddressInput
              value={form.addressSearch}
              placeholder="Search city or address"
              onLocationChange={value => setForm(current => ({ ...current, addressSearch: value }))}
              onLocationSelect={handlePlaceSelect}
            />
          </Field>
        </Box>

        <FormGrid>
          <Field label="City">
            <Input
              value={form.city}
              onChange={event => setForm(current => ({ ...current, city: event.target.value }))}
            />
          </Field>
          <Field label="State">
            <Input
              value={form.state}
              onChange={event => setForm(current => ({ ...current, state: event.target.value }))}
            />
          </Field>
          <Field label="Position (1 to 5)">
            <Input
              type="number"
              min={1}
              max={5}
              value={form.index}
              onChange={event =>
                setForm(current => ({ ...current, index: Number(event.target.value) }))
              }
            />
          </Field>
          <Field label="Max cars">
            <Input
              type="number"
              min={1}
              max={50}
              value={form.amount}
              onChange={event =>
                setForm(current => ({ ...current, amount: Number(event.target.value) }))
              }
            />
          </Field>
          <Field label="Latitude">
            <Input value={form.latitude} readOnly />
          </Field>
          <Field label="Longitude">
            <Input value={form.longitude} readOnly />
          </Field>
        </FormGrid>

        <ModalActions>
          <Button text="Cancel" theme="secondary" type="button" onClick={closeModal} />
          <Button text="Save" type="button" onClick={saveForm} isLoading={creating || updating} />
        </ModalActions>
      </Modal>
    </PageContainer>
  );
}
